import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Card, DataTable } from '@w7/ui'
import { bookmakerApi, bmErrorMessage, unwrapBmList } from '@/api/bookmaker'
import { BmPage } from './BmPage'

export function UserListPage() {
  const navigate = useNavigate()
  const [rows, setRows] = useState<Record<string, unknown>[]>([])
  const [error, setError] = useState<string | null>(null)
  const [msg, setMsg] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function load() {
    setBusy(true)
    setError(null)
    try {
      const data = await bookmakerApi.getAllUsers({ level: 'User' })
      setRows(unwrapBmList(data))
      const o = data && typeof data === 'object' ? (data as Record<string, unknown>) : {}
      if (typeof o.msg === 'string' && !unwrapBmList(data).length) setMsg(o.msg)
      else setMsg(null)
    } catch (e) {
      setError(bmErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  async function onDelete(row: Record<string, unknown>) {
    const id = String(row._id ?? row.id ?? '')
    const name = String(row.userName ?? row.username ?? id)
    if (!id) return
    if (!window.confirm(`Delete user ${name}?`)) return
    setBusy(true)
    setError(null)
    try {
      const res = (await bookmakerApi.deleteUser({ _id: id })) as Record<string, unknown>
      if (res.value === false) {
        setError(String(res.msg ?? 'Delete failed'))
      } else {
        await load()
      }
    } catch (e) {
      setError(bmErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <BmPage
      title="Users"
      description="Lab User list — Username, Email, Status"
      error={error}
      actions={
        <>
          <Button onClick={() => void load()} disabled={busy}>
            Refresh
          </Button>
          <Button onClick={() => navigate('/createuser')}>Create</Button>
        </>
      }
    >
      {msg ? <p style={{ margin: 0, opacity: 0.75 }}>{msg}</p> : null}
      <Card>
        <DataTable
          label="Users"
          columns={[
            {
              id: 'userName',
              header: 'Username',
              cell: (r: Record<string, unknown>) => String(r.userName ?? r.username ?? '—'),
            },
            {
              id: 'email',
              header: 'Email',
              cell: (r: Record<string, unknown>) => String(r.email ?? '—'),
            },
            {
              id: 'status',
              header: 'Status',
              cell: (r: Record<string, unknown>) => String(r.status ?? '—'),
            },
            {
              id: 'actions',
              header: 'Action',
              cell: (r: Record<string, unknown>) => (
                <span style={{ display: 'inline-flex', gap: 8 }}>
                  <Button
                    disabled={busy}
                    onClick={(e) => {
                      e.stopPropagation()
                      const id = String(r._id ?? r.id ?? '')
                      if (id) navigate(`/edituser/${id}`)
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    disabled={busy}
                    onClick={(e) => {
                      e.stopPropagation()
                      void onDelete(r)
                    }}
                  >
                    Delete
                  </Button>
                </span>
              ),
            },
          ]}
          rows={rows}
          loading={busy}
          rowKey={(r) => String(r._id ?? r.id ?? r.userName ?? Math.random())}
        />
      </Card>
    </BmPage>
  )
}
